/**
 * React Query hook for Friend Stats API
 * Uses TanStack Query for caching, loading states, and infinite scroll pagination
 */

import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { fetchFriendStats } from '../helpers/friendsApi';
import { CHALLENGE_STATUS } from '../constants/challengeStatus.constants';
import { STALE_TIME_FRIEND_STATS_MS } from '../constants/reactQuery.constants';

/**
 * Hook to fetch friend statistics with infinite scroll pagination
 * @param friendId - Friend ID to fetch stats for
 */
export function useFriendStats(friendId: string | null) {
  const apiClient = useApiClient();

  const debug = process.env.EXPO_PUBLIC_FRIEND_STATS_DEBUG === '1';

  const query = useInfiniteQuery({
    queryKey: ['friendStats', friendId, debug],
    queryFn: async ({ pageParam = 0 }) => {
      if (!friendId) return null;

      return fetchFriendStats(friendId, pageParam, 10, apiClient, { debug });
    },
    enabled: !!friendId,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.pagination.hasMore) return undefined;
      return lastPage.pagination.page + 1;
    },
    initialPageParam: 0,
    staleTime: STALE_TIME_FRIEND_STATS_MS,
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false, // avoid duplicate request when re-entering screen
  });

  // Extract summary from first page (memoized to prevent unnecessary re-renders)
  const summary = useMemo(
    () => query.data?.pages[0]?.summary || null,
    [query.data?.pages]
  );

  // Flatten pick history from all pages and sort (ACTIVE first - frontend is source of truth)
  const pickHistory = useMemo(() => {
    const flat = query.data?.pages.flatMap((page) => page?.pickHistory || []) || [];
    return flat.sort((a, b) => {
      // ACTIVE first (frontend is source of truth)
      if (a.status === CHALLENGE_STATUS.ACTIVE && b.status !== CHALLENGE_STATUS.ACTIVE) return -1;
      if (a.status !== CHALLENGE_STATUS.ACTIVE && b.status === CHALLENGE_STATUS.ACTIVE) return 1;
      // Then by date (newest first)
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [query.data?.pages]);

  // Helper flags for UI state management
  const isInitialLoading = query.isLoading && !query.data;
  const hasHistory = pickHistory.length > 0;
  const hasNoHistory = !isInitialLoading && summary !== null && !hasHistory;

  return {
    ...query,
    summary,
    pickHistory,
    isInitialLoading,
    hasHistory,
    hasNoHistory,
  };
}


